/*
  bb-overlay-coordinator.js — one page lock for overlays that can be open at
  the same time, and a signal for "the shopper is using the cart".

  Page lock. The theme locks the page with body.overflow-hidden and
  html[scroll-lock]. Overlays can stack (the cart drawer with the coupon
  wallet over it; a promotion and the cart), and each used to remove the
  lock when it closed, unlocking the page under the one still open.
  BBOverlay.lock(id) / unlock(id) keep a set of holders: the first records
  the page as it was (scroll position, any lock already there, inline
  overflow) and the last puts it back. While a holder remains, a lock
  removed by a script that doesn't use these calls is put back.

  Cart activity. BBOverlay.cartBusy() is true while a cart drawer, cart
  tool, variant editor, coupon overlay or summary sheet is open, or a
  checkout is starting (until the shopper is evidently still on the page). whenCartIdle(fn, ms) runs fn once the cart has been
  quiet for ms, starting the wait again whenever the cart gets busy.
  'bb:overlay:change' { id, open } fires when a holder joins or leaves.
*/
(function () {
  'use strict';

  if (window.BBOverlay) return;

  var root = document.documentElement;
  var holders = new Map(); // id → element it belongs to (or null)
  var saved = null;
  var repairing = false;
  var leaving = false;
  var leavingAt = 0;
  var STILL_HERE_MS = 2000;

  // Cart surfaces that are open without holding a lock of their own.
  var CART_OPEN = [
    'cart-drawer:not([data-state="closed"])',
    '[data-edit-cart-popup].open',
    '[data-edit-cart-popup].active',
    '#bb-loc-popup.is-open',
    '#bb-loc-drilldown.is-open',
    '#bb-del-popup.is-open',
    '#bb-summary-popup.is-open',
    '#bb-coupon-popup-overlay.is-visible',
    '#bb-glow-sheet[data-state="open"]',
    '#bb-glow-sheet[data-state="opening"]',
  ].join(', ');

  function prune() {
    holders.forEach(function (element, id) {
      if (element && !element.isConnected) holders.delete(id);
    });
  }

  function apply() {
    repairing = true;
    document.body.classList.add('overflow-hidden');
    root.setAttribute('scroll-lock', '');
    repairing = false;
  }

  function emit(id, open) {
    document.dispatchEvent(new CustomEvent('bb:overlay:change', { detail: { id: id, open: open } }));
  }

  function lock(id, element) {
    if (!id || !document.body) return;
    prune();
    if (!holders.size) {
      saved = {
        scrollY: window.scrollY,
        bodyClass: document.body.classList.contains('overflow-hidden'),
        rootAttr: root.hasAttribute('scroll-lock'),
        bodyOverflow: document.body.style.overflow,
      };
    }
    var known = holders.has(id);
    holders.set(id, element || null);
    apply();
    if (!known) emit(id, true);
  }

  function unlock(id) {
    if (!holders.has(id)) return;
    holders.delete(id);
    prune();
    if (holders.size) {
      apply();
      emit(id, false);
      return;
    }
    var page = saved;
    saved = null;
    repairing = true;
    if (!page || !page.bodyClass) document.body.classList.remove('overflow-hidden');
    if (!page || !page.rootAttr) root.removeAttribute('scroll-lock');
    document.body.style.overflow = page ? page.bodyOverflow : '';
    repairing = false;
    if (page && Math.abs(window.scrollY - page.scrollY) > 1) window.scrollTo(0, page.scrollY);
    emit(id, false);
  }

  // A script that doesn't use lock()/unlock() closed its own overlay and
  // removed the shared lock: its lock is gone, ours are not.
  function guard() {
    if (repairing || !holders.size) return;
    prune();
    if (!holders.size) return;
    var lostClass = !document.body.classList.contains('overflow-hidden');
    var lostAttr = !root.hasAttribute('scroll-lock');
    if (!lostClass && !lostAttr) return;
    if (saved) {
      if (lostClass) saved.bodyClass = false;
      if (lostAttr) saved.rootAttr = false;
    }
    apply();
  }

  function cartBusy() {
    if (leaving) return true;
    prune();
    var held = false;
    holders.forEach(function (element, id) {
      if (id.indexOf('cart:') === 0) held = true;
    });
    return held || !!document.querySelector(CART_OPEN);
  }

  function whenCartIdle(fn, quietMs) {
    var timer = null;
    var poll = null;
    var done = false;
    function stop() {
      done = true;
      clearTimeout(timer);
      clearInterval(poll);
      document.removeEventListener('bb:overlay:change', check);
    }
    function check() {
      if (done) return;
      if (cartBusy()) {
        clearTimeout(timer);
        timer = null;
        return;
      }
      if (timer) return;
      timer = setTimeout(function () {
        timer = null;
        if (done || cartBusy()) return;
        stop();
        fn();
      }, quietMs);
    }
    document.addEventListener('bb:overlay:change', check);
    // Some cart surfaces only show their state in the page; look regularly.
    poll = setInterval(check, 300);
    check();
    return stop;
  }

  // Checkout starting (cart checkout, Buy it now, express buttons). It stays
  // "starting" for as long as the navigation may be pending — no time limit —
  // and ends when the shopper is evidently still here: they interact with the
  // page again a moment later (the navigation failed or was cancelled, or a
  // wallet sheet was closed), or come back with Back.
  function markLeaving() {
    leaving = true;
    leavingAt = Date.now();
    emit('cart:checkout', true);
  }

  function clearLeaving() {
    if (!leaving) return;
    leaving = false;
    emit('cart:checkout', false);
  }

  ['pointerdown', 'touchstart', 'keydown'].forEach(function (type) {
    document.addEventListener(
      type,
      function (event) {
        if (leaving && event.isTrusted && Date.now() - leavingAt > STILL_HERE_MS) clearLeaving();
      },
      { capture: true, passive: true }
    );
  });

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || !event.target.closest) return;
    var control = event.target.closest(
      '[name="checkout"], [data-bb-glow-checkout], a[href$="/checkout"], a[href*="/checkout?"], .shopify-payment-button__button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart'
    );
    if (control) markLeaving();
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    var submitter = event.submitter;
    if ((submitter && submitter.name === 'checkout') || (form.querySelector && form.querySelector('input[name="return_to"][value*="checkout"]'))) {
      markLeaving();
    }
  });

  // Back from checkout (bfcache): the shopper is on the page again.
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) clearLeaving();
  });

  var observer = new MutationObserver(guard);
  observer.observe(root, { attributes: true, attributeFilter: ['scroll-lock'] });
  if (document.body) {
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    });
  }

  window.BBOverlay = {
    lock: lock,
    unlock: unlock,
    holders: function () {
      prune();
      return Array.from(holders.keys());
    },
    cartBusy: cartBusy,
    whenCartIdle: whenCartIdle,
    markLeaving: markLeaving,
    clearLeaving: clearLeaving,
  };
})();
